import { is } from 'date-fns/locale';
import { intlFormat, parse, subDays, addDays, format, isBefore, differenceInCalendarDays } from 'date-fns';
import { Country } from '@/domainLogic/index.ts';
import { isGermanLocale, isIcelandLocale, isUsLocale } from '@/app/common/measures/locale.ts';

export const FLIGHTS_DATES_FORMAT = 'dd/MM/yy#HH:mm';
export const SERVICES_DATES_FORMAT = 'yyyyMMdd';
export const URL_DATE_FORMAT = 'yyyyMMdd';
export const SHOPPING_API_DATE_FORMAT = 'yyyy-MM-dd';
export const PRICE_CALENDAR_API_DATE_FORMAT = 'yyyy-MM-dd';

export const PRICE_CALENDAR_RANGE_DAYS_MOBILE = 2;
export const PRICE_CALENDAR_RANGE_DAYS_TABLET = 4;
export const PRICE_CALENDAR_RANGE_DAYS_DESKTOP = 6;

// TODO TEMP type definition until there is an exported type from date-fns.
export type IntlDateTimeFormatOptions = {
  localeMatcher?: 'lookup' | 'best fit';
  weekday?: 'narrow' | 'short' | 'long';
  era?: 'narrow' | 'short' | 'long';
  year?: 'numeric' | '2-digit';
  month?: 'numeric' | '2-digit' | 'narrow' | 'short' | 'long';
  day?: 'numeric' | '2-digit';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  second?: 'numeric' | '2-digit';
  timeZoneName?: 'short' | 'long';
  formatMatcher?: 'basic' | 'best fit';
  hour12?: boolean;
  timeZone?: string;
};

export function shoppingApiDateFormat(date: string, dateFormat: string) {
  return format(parse(date, SHOPPING_API_DATE_FORMAT, new Date()), dateFormat);
}

export function shoppingApiDateFormatFromLocale(date: string, locale: string, customFormat?: string) {
  return shoppingApiDateFormat(date, getCancellationDateformat(locale, customFormat));
}

export function changeDateFormat(date: string, originFormat: string, destinationFormat: string) {
  const originData = parse(date, originFormat, new Date());
  return format(originData, destinationFormat);
}

export function changeUrlDepartureIntervalsFormat(dateString: string) {
  const year = dateString.slice(0, 4);
  const month = dateString.slice(4, 6);
  const day = dateString.slice(6, 8);

  return `${year}-${month}-${day}`;
}

export function basicFlightDateFormat(date: string, formatting: string) {
  return format(parse(date, FLIGHTS_DATES_FORMAT, new Date()), formatting);
}

export function getNights(checkin: string, checkout: string) {
  const checkinDate = parse(checkin, SERVICES_DATES_FORMAT, new Date());
  const checkoutDate = parse(checkout, SERVICES_DATES_FORMAT, new Date());
  return differenceInCalendarDays(checkoutDate, checkinDate);
}

export function getNightsFromToday(toDate: string) {
  const toDateParsed = parse(toDate, SHOPPING_API_DATE_FORMAT, new Date());
  return differenceInCalendarDays(toDateParsed, new Date());
}

export function getTripNights(checkin: string, checkout: string) {
  const checkinDate = parse(checkin, FLIGHTS_DATES_FORMAT, new Date());
  const checkoutDate = parse(checkout, FLIGHTS_DATES_FORMAT, new Date());
  return differenceInCalendarDays(checkoutDate, checkinDate);
}

export function getTripDates(locale: string, tripCheckin: string, tripCheckout: string) {
  const checkinDate = parse(tripCheckin, SERVICES_DATES_FORMAT, new Date());
  const checkoutDate = parse(tripCheckout, SERVICES_DATES_FORMAT, new Date());
  const checkinCardFormatted = intlFormat(
    checkinDate,
    {
      month: 'short',
      day: 'numeric',
    },
    {
      locale: getCultureCode(locale),
    }
  );
  const checkoutCardFormatted = intlFormat(
    checkoutDate,
    {
      month: 'short',
      day: 'numeric',
    },
    {
      locale: getCultureCode(locale),
    }
  );
  return { checkinCardFormatted, checkoutCardFormatted };
}

export function getTripDatesPriceCalendar(tripCheckin: string, tripCheckout: string) {
  const checkinDate = parse(tripCheckin, SERVICES_DATES_FORMAT, new Date());
  const checkoutDate = parse(tripCheckout, SERVICES_DATES_FORMAT, new Date());
  const checkinFormatted = format(checkinDate, PRICE_CALENDAR_API_DATE_FORMAT);
  const checkoutFormatted = format(checkoutDate, PRICE_CALENDAR_API_DATE_FORMAT);
  return { checkinFormatted, checkoutFormatted };
}

export function getHotelCheckinCheckoutDates(
  locale: string,
  checkin: string,
  checkout: string,
  originFormat: string = SERVICES_DATES_FORMAT
) {
  return getCheckinCheckoutDates(locale, checkin, checkout, originFormat);
}

export function getHotelCheckinCheckoutDatesShort(
  locale: string,
  checkin: string,
  checkout: string,
  originFormat: string = SERVICES_DATES_FORMAT
) {
  return getCheckinCheckoutDates(locale, checkin, checkout, originFormat, { day: 'numeric', month: 'short' });
}

export function getFlightDates(
  locale: string,
  checkin: string,
  checkout: string,
  originFormat: string = SERVICES_DATES_FORMAT
) {
  return getCheckinCheckoutDates(locale, checkin, checkout, originFormat);
}
export function getCheckinCheckoutDates(
  locale: string,
  checkin: string,
  checkout: string,
  originFormat: string,
  formatOptions: IntlDateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }
) {
  const checkinDate = parse(checkin, originFormat, new Date());
  const checkoutDate = parse(checkout, originFormat, new Date());
  if (isIcelandLocale(locale)) {
    // intlFormat does not support Icelandic locale, so we use date-fns format directly
    const icelandFormat = formatOptions.weekday ? 'EEE d MMM' : 'd MMM';
    const formattedCheckinDate = format(checkinDate, icelandFormat, { locale: is });
    const formattedCheckoutDate = format(checkoutDate, icelandFormat, { locale: is });
    return { checkin: formattedCheckinDate, checkout: formattedCheckoutDate };
  }
  const checkinFormatted = intlFormat(checkinDate, formatOptions, {
    locale: getCultureCode(locale),
  });
  const checkoutFormatted = intlFormat(checkoutDate, formatOptions, {
    locale: getCultureCode(locale),
  });
  return { checkin: checkinFormatted, checkout: checkoutFormatted };
}

export type DateRange = { from?: string; to?: string; nights?: number };

export function getDateRange(
  date: string,
  originFormat: string,
  resultFormat: string,
  add: number,
  sub: number
): DateRange {
  const parsedDate = parse(date, originFormat, new Date());

  const from = subDays(parsedDate, sub);
  const to = addDays(parsedDate, add);

  return {
    from: format(from, resultFormat),
    to: format(to, resultFormat),
  };
}

export function getDateRangeExcludingPastDates(
  date: string,
  originFormat: string,
  resultFormat: string,
  add: number,
  sub: number
): DateRange {
  const parsedDate = parse(date, originFormat, new Date());
  const tempFrom = subDays(parsedDate, sub);
  const minDate = addDays(new Date(), 3); // Today + 3 days

  const isFromBeforeMinDate = isBefore(tempFrom, minDate);

  const from = isFromBeforeMinDate ? minDate : tempFrom;
  const baseDateForTo = isFromBeforeMinDate ? minDate : parsedDate;
  const to = addDays(baseDateForTo, add);

  return {
    from: format(from, resultFormat),
    to: format(to, resultFormat),
  };
}

export function addDaysWithFormat(date: string, dateFormat: string, days: number) {
  const newDate = addDays(parse(date, dateFormat, new Date()), days);
  return format(newDate, dateFormat);
}

/**
 * Cancellation copy communicates the deadline with the word "before": the first day the
 * free-cancellation window no longer applies, i.e. the day AFTER the last cancellable day (`upTo`).
 * e.g. cancellable through 2026-06-29 (inclusive) → "before 2026-06-30". This aligns the wording with
 * checkout ("cancellation available before X"). Returns a yyyy-MM-dd string that each surface then
 * formats with its own date formatter.
 */
export function getCancellationBeforeDate(upTo: string) {
  return addDaysWithFormat(upTo, SHOPPING_API_DATE_FORMAT, 1);
}

export function getFlightDateFromISO(isoDate: string) {
  return `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}/${isoDate.slice(2, 4)}#${isoDate.slice(11, 16)}`;
}

export function getISODateFromFlightDate(flightDate: string) {
  const parsedDate = parse(flightDate, FLIGHTS_DATES_FORMAT, new Date());
  return format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss");
}

export const getCancellationDateformat = (locale: string, customFormat?: string) => {
  if (isGermanLocale(locale)) return 'dd.MM.yy';
  if (isUsLocale(locale)) return 'MM/dd/yy';
  if (customFormat) return customFormat;
  return 'd/MM/yy';
};

export function getCultureCode(locale: string) {
  return locale.replace('_', '-');
}

export function formatCancellationDate(upTo: string, locale: string) {
  const upToDate = parse(upTo, SHOPPING_API_DATE_FORMAT, new Date());
  return intlFormat(
    upToDate,
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    },
    {
      locale: getCultureCode(locale),
    }
  );
}

export function formatFlightArrivalDay(locale: string, date: string) {
  const upToDate = parse(date, FLIGHTS_DATES_FORMAT, new Date());
  return intlFormat(
    upToDate,
    {
      day: 'numeric',
      month: 'short',
    },
    {
      locale: getCultureCode(locale),
    }
  );
}

export function formatDate(locale: string, date: Date, formatOptions: IntlDateTimeFormatOptions) {
  return intlFormat(date, formatOptions, {
    locale: getCultureCode(locale),
  });
}

export const from24hto12h = (time?: string) => {
  if (!time) {
    return {
      m: '',
      h: '',
      s: '',
    };
  }

  const [hour, minute] = time.split(':');
  let hour12 = parseInt(hour, 10);
  const minute12 = parseInt(minute, 10);

  // Handle 24:00 as 00:00 (midnight)
  if (hour12 === 24) {
    hour12 = 0;
  }

  const suffix = hour12 >= 12 ? 'PM' : 'AM';
  const hour12Formatted = (((hour12 + 11) % 12) + 1).toString();

  return {
    m: minute12.toString().padStart(2, '0'),
    h: hour12Formatted,
    s: suffix,
  };
};

export const formatCheckinTime = (hour?: string, country?: Country) => {
  if (country === Country.US) {
    const { m, h, s } = from24hto12h(hour);
    return {
      hour: h,
      min: `${m} ${s ? ` ${s}` : ''}`,
    };
  }
  return {
    hour: hour?.substring(0, 2) || '',
    min: hour?.substring(3) || '',
  };
};
